import { logger } from './secureLogger';

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  additionalData?: any;
}

export class AppError extends Error {
  public userMessage: string;
  public statusCode: number;
  public context?: ErrorContext;

  constructor(
    message: string,
    userMessage: string,
    statusCode: number = 500,
    context?: ErrorContext
  ) {
    super(message);
    this.name = 'AppError';
    this.userMessage = userMessage;
    this.statusCode = statusCode;
    this.context = context;
  }
}

// Generic error handler that doesn't expose sensitive system details
export const handleError = (
  error: any,
  context?: ErrorContext,
  fallbackMessage: string = 'An unexpected error occurred'
): AppError => {
  // Log the full error securely
  logger.error('Application error', {
    message: error?.message,
    stack: error?.stack,
    name: error?.name,
    statusCode: error?.status || error?.statusCode,
    context
  });

  // Determine user-friendly message based on error type
  let userMessage = fallbackMessage;
  let statusCode = 500;

  if (error instanceof AppError) {
    return error;
  }

  // Handle common PocketBase errors
  if (error?.status) {
    statusCode = error.status;
    switch (error.status) {
      case 400:
        userMessage = 'The request contains invalid data. Please check your input.';
        break;
      case 401:
        userMessage = 'You need to log in to access this feature.';
        break;
      case 403:
        userMessage = 'You don\'t have permission to perform this action.';
        break;
      case 404:
        userMessage = 'The requested resource was not found.';
        break;
      case 409:
        userMessage = 'This operation conflicts with existing data.';
        break;
      case 429:
        userMessage = 'Too many requests. Please wait a moment and try again.';
        break;
      case 500:
      case 502:
      case 503:
        userMessage = 'The service is temporarily unavailable. Please try again later.';
        break;
      default:
        userMessage = fallbackMessage;
    }
  }

  // Handle network errors
  if (error?.message?.includes('fetch') || error?.message?.includes('network')) {
    userMessage = 'Network error. Please check your internet connection.';
    statusCode = 0;
  }

  // Handle authentication errors specifically
  if (error?.message?.includes('auth') || error?.message?.includes('token')) {
    userMessage = 'Authentication failed. Please log in again.';
    statusCode = 401;
  }

  return new AppError(
    error?.message || 'Unknown error',
    userMessage,
    statusCode,
    context
  );
};

// Handle async operations with proper error handling
export const safeAsync = async <T>(
  operation: () => Promise<T>,
  context?: ErrorContext,
  fallbackMessage?: string
): Promise<{ data: T | null; error: AppError | null }> => {
  try {
    const data = await operation();
    return { data, error: null };
  } catch (error) {
    const appError = handleError(error, context, fallbackMessage);
    return { data: null, error: appError };
  }
};

// React error boundary handler
export const errorBoundaryHandler = (error: Error, errorInfo: any) => {
  logger.error('React error boundary caught error', {
    message: error.message,
    stack: error.stack,
    componentStack: errorInfo.componentStack
  });
};

// Admin action error handler with security logging
export const handleAdminError = (
  error: any,
  adminId: string,
  action: string,
  targetId?: string,
  additionalData?: any
) => {
  logger.adminAction(action, adminId, targetId, {
    error: error?.message,
    success: false,
    ...additionalData
  });

  return handleError(error, {
    component: 'AdminAction',
    action,
    userId: adminId,
    additionalData: { targetId, ...additionalData }
  });
};